import React, { useEffect, useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Container,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { clientBasedFiles } from "./utils/plant_to_files_mapping";
import { CalendarMonth, Functions, Key } from "@mui/icons-material";
import styled from "@emotion/styled";
import ContainedButton from "../../common/components/Buttons/ContainedButton";
import { useDispatch, useSelector } from "react-redux";
import { get_auth_user_data, get_selected_plant_name } from "../../redux/auth/auth.selectors";
import InputTable from "./components/InputTable";
import { read, utils } from "xlsx";
import { v4 } from "uuid";
import SnackbarWithMessage from "../../common/components/Snackbars/SnackbarWithMessage";
import { useTheme } from "@emotion/react";
import ParametersForm from "./components/ParametersForm";
import { file_validator } from "./utils/file_validator";
import { generate_schedule_action } from "../../redux/app/app.actions";
import { useNavigate, useParams } from "react-router";
import POBackLogTabs from "./components/PO_BackLog_Tabs";
import { useSearchParams } from "react-router-dom";

// Define the client options based on the available keys in clientBasedFiles
// const clientOptions = Object.keys(clientBasedFiles);

// type ClientData = {
//     pre_commit?: PreCommit;
//     final_commit?: FinalCommit;
//     monthlyTitles?: MonthlyTitles;
//     masterTitles?: MasterTitles;
// };

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  // backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#938f96",
  boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  ...(ownerState.active && {
    backgroundColor: "#084298",
  }),
  ...(ownerState.completed && {
    backgroundColor: "#084298",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <CalendarMonth />,
    2: <Key />,
    3: <Functions />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const handleClientBasedFiles = (selected_plant, commit_key) => {
  const value = clientBasedFiles[selected_plant];
  if ("pre_commit" in value && commit_key) {
    // Here TypeScript knows that value is of type Commit.
    // You can safely access value.pre_commit and value.final_commit.
    const monthlyTitles = value[commit_key || "pre_commit"].monthlyTitles;
    const masterTitles = value[commit_key || "pre_commit"].masterTitles;
    return {
      monthlyTitles,
      masterTitles,
    };
  } else if ("monthlyTitles" in value) {
    // Here TypeScript knows that value is of type Titles.
    // You can safely access value.monthlyTitles and value.masterTitles.
    const { masterTitles, monthlyTitles } = value;
    return { masterTitles, monthlyTitles };
  } else {
    const monthlyTitles = {};
    const masterTitles = {};
    return {
      monthlyTitles,
      masterTitles,
    };
  }
};

function getCommitType(selectedPlant) {
  const plantData = clientBasedFiles[selectedPlant];

  // if (!plantData) {
  //     return;  // or throw an error
  // }

  if ("pre_commit" in plantData) {
    return "pre_commit";
  } else if ("final_commit" in plantData) {
    return "final_commit";
  }

  return; // or throw an error
}

// type numeric_parameter = {
//   min: number;
//   max: number;
//   value: number;
//   type: "number";
// };

// export type ParameterType = {
//   [key: string]: numeric_parameter;
// };

const InputFormContainer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { selected_plant } = useSelector(get_selected_plant_name);

  const user = useSelector(get_auth_user_data);

  const [client_files, set_client_files] = useState(handleClientBasedFiles(selected_plant));

  const [commit_type, set_commit_type] = useState(getCommitType(selected_plant));

  const [ready_to_generate_schedule, set_ready_to_generate_schedule] = useState(false);

  useEffect(() => {
    set_client_files(handleClientBasedFiles(selected_plant, commit_type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit_type]);

  const handle_client_file_update = (
    monthly_or_master,
    key_to_be_updated,
    file,
  ) => {
    if (file) {
      //   console.log("reading uploaded file");
      const reader = new FileReader();
      reader.onload = (event) => {
        const workbook = read(event.target?.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

        // Convert to array of objects
        const headers = jsonData[0];
        const validation = file_validator(selected_plant, key_to_be_updated, headers);
        console.log({ validation });
        if (!validation.isValid) {
          set_open_snackbar_with_message({
            message: `Uploaded file is missing the required column(s): \n${validation?.missing_keys.join(", ")}`,
            open: true,
            severity: "error"
          });
          return;
        }
        const rows = jsonData.slice(1);
        const formattedData = rows.map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index];
          });
          obj.id = v4();
          return obj;
        });
        // console.log(formattedData);
        const columns = headers.map((header) => ({
          field: header,
        }));
        columns.push({ field: "id" });
        const table_data = formattedData;
        set_client_files((prev) => {
          const temporary_obj = { ...prev };
          //   console.log(temporary_obj, temporary_obj[monthly_or_master]);
          temporary_obj[monthly_or_master][key_to_be_updated].file = file;
          temporary_obj[monthly_or_master][key_to_be_updated].columns = columns;
          temporary_obj[monthly_or_master][key_to_be_updated].table_data = table_data;
          temporary_obj[monthly_or_master][key_to_be_updated].status = true;
          return temporary_obj;
        });
      };
      reader.readAsBinaryString(file);
    }
  };

  const { section } = useParams();
  const { tab } = useParams();
  const [searchParams,] = useSearchParams();
  // console.log({ searchParams });
  const targeted_date_for_completion_of_analysis = searchParams.get("targeted_date_for_completion_of_analysis");

  const steps = ["Upload Daily Input", "Upload Master Input", "Set Objective Functions"];

  const [activeStep, setActiveStep] = useState(() => {
    if (section === "parameters") return (2);
    else if (section === "monthly") return (1);
    else return (0);
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    switch (activeStep) {
      case 0: {
        navigate(`/input/daily/${tab ? tab : "po"}${targeted_date_for_completion_of_analysis ? `?targeted_date_for_completion_of_analysis=${targeted_date_for_completion_of_analysis}` : ``}`);
        return;
      }
      case 1: {
        navigate("/input/master");
        return;
      }
      case 2: {
        navigate("/input/parameters");
        return;
      }
      default: {
        navigate(`/input/daily/${tab ? tab : "po"}${targeted_date_for_completion_of_analysis ? `targeted_date_for_completion_of_analysis=${targeted_date_for_completion_of_analysis}` : ``}`);
        return;
      }
    }
    // if (activeStep === 0) {
    //   navigate("/input/daily");
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    // console.log("client_file_updated", client_files);
    const monthly_keys = Object.keys(client_files.monthlyTitles);
    const master_keys = Object.keys(client_files.masterTitles);
    for (let key of monthly_keys) {
      if (
        client_files.monthlyTitles[key].visible
        && !client_files.monthlyTitles[key].file
        && !client_files.monthlyTitles[key]?.optional // uncomment this line if you want to ignore the optional files...
      ) {
        console.log("some monthly input files are missing... aborting the process of ready to generate schedule...");
        return;
      }
    }
    for (let key of master_keys) {
      if (
        client_files.masterTitles[key].visible
        && !client_files.masterTitles[key].file
        && !client_files.masterTitles[key]?.optional // uncomment this line if you want to ignore the optional files...
      ) {
        console.log("some master input files are missing... aborting the process of ready to generate schedule...");
        return;
      }
    }
    console.log("all checks passed, ready to generate schedule now!");
    set_ready_to_generate_schedule(true);
  }, [client_files]);

  const [parameters, set_parameters] = useState({
    sla: {
      include_in_form_data: true,
      min: 0,
      max: 10,
      value: 1,
      type: "number",
    },
    sample_tested_per_week: {
      include_in_form_data: true,
      min: 0,
      max: 10,
      value: 1,
      type: "number",
    },
    mios_processed: {
      include_in_form_data: false,
      min: 0,
      max: 10,
      value: 1,
      type: "number"
    }
  });

  const handle_slider_change = (parameterKey, newValue) => {
    set_parameters((prevParameters) => ({
      ...prevParameters,
      [parameterKey]: {
        ...prevParameters[parameterKey],
        value: newValue,
      },
    }));
  };

  const handle_input_change = (parameterKey, newValue) => {
    set_parameters((prevParameters) => ({
      ...prevParameters,
      [parameterKey]: {
        ...prevParameters[parameterKey],
        value: parseFloat(newValue),
      },
    }));
  };
  const [open_snackbar_with_message, set_open_snackbar_with_message] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handle_submit = (event) => {
    event.preventDefault();
    if (!ready_to_generate_schedule) {
      set_open_snackbar_with_message({ open: true, message: "Kindly upload all the files before proceeding!", severity: "error" });
      // return;
    }
    const form_data = new FormData();
    const monthly_keys = Object.keys(client_files.monthlyTitles);
    monthly_keys.forEach((key_name) => {
      form_data.append(client_files.monthlyTitles[key_name]?.form_data_name, client_files.monthlyTitles[key_name]?.file);
    });
    const master_keys = Object.keys(client_files.masterTitles);
    master_keys.forEach((key_name) => {
      form_data.append(client_files.masterTitles[key_name]?.form_data_name, client_files.masterTitles[key_name]?.file);
    });
    form_data.append("client_id", user.selected_plant);
    form_data.append("uuid", v4());
    form_data.append("qc_schedule_id", v4());
    form_data.append("user", JSON.stringify({ name: user.name }));
    const date = new Date();
    // format date to DD-MM-YYYY-HH-MM-SS
    const formattedDate = `${date.getDate()}-${date.getMonth() + 1
      }-${date.getFullYear()}-${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
    form_data.append("schedule_name", `${user?.name || "Ripik Optimus"}-QC${formattedDate}`);
    const params = Object.keys(parameters);
    params.forEach((key) => {
      if (parameters[key].include_in_form_data) {
        form_data.append(key, parameters[key].value.toString());
      }
    });

    try {
      dispatch(generate_schedule_action({ endpoint: "qc_process", form_data, navigate }));
      // const backend_url = process.env.REACT_APP_BACKEND_URL;
      // const request_options = {
      //   method: 'POST',
      //   body: form_data,
      //   redirect: 'follow'
      // };

      // fetch("https://35.200.198.152.nip.io/qc_process", request_options)
      //   .then(response => response.json())
      //   .then(result => console.log(result))
      //   .catch(error => console.log('error', error));
      // fetch(`${backend_url}/qc_process`, {
      //   method: "POST",
      //   body: form_data,
      //   redirect: 'follow',
      // }).then((response) => {
      //   return response.json();
      // }).then((result) => {
      //   console.log({ result });
      //   localStorage.setItem(`qc_schedule_${user.selected_plant}_output`, JSON.stringify(result));
      // }).catch((reason) => {
      //   console.log({ error: reason });
      // }).finally(() => {
      //   console.log("request ended");
      // });
    } catch (error) {
      console.log({ error });
    }
    // client_files.monthlyTitles?.
    // Handle form submission
    // console.log({parameters});
    // console.log({client_files})
  };

  return (
    <Box className="">
      <SnackbarWithMessage {...open_snackbar_with_message} onClose={() => {
        set_open_snackbar_with_message({
          open: false,
          message: "",
          severity: "info"
        });
      }} />
      <div
        className="p-8"
        style={{
          width: "100%",
          color: "black",
          fontSize: "28px",
          fontFamily: "Poppins",
          fontWeight: 400,
          lineHeight: "36px",
          wordWrap: "break-word",
        }}
      >
        QC Scheduler
        <br />
      </div>
      <Box className="flex justify-center items-start">
        <Container className="p-2">
          <Stepper activeStep={activeStep}>
            {steps.map((step, idx) => (
              <Step key={`${idx}-${step}-input-form-${idx}`}>
                <StepLabel StepIconComponent={ColorlibStepIcon} StepIconProps={{}}>
                  {!isMobile && step}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box className="flex justify-end my-2">
            <ContainedButton
              handleClick={() => {
                alert("Sample Files Download Clicked");
              }}
            >
              Sample Files
            </ContainedButton>
          </Box>
          <Box className="p-4" boxShadow="0px 2px 6px rgba(0, 0, 0, 0.15)" borderRadius="4px" bgcolor="white">
            {commit_type && (
              <Select
                value={commit_type}
                onChange={(e) => set_commit_type(e.target.value)}
              >
                <MenuItem value="pre_commit">Pre-Commit</MenuItem>
                <MenuItem value="final_commit">Final Commit</MenuItem>
              </Select>
            )}
            {activeStep === 0 ? (
              <Box>
                <POBackLogTabs tab={tab} targeted_date_for_completion_of_analysis={targeted_date_for_completion_of_analysis} />
                {
                  // <InputTable
                  //   stage_name={steps[activeStep]}
                  //   input_objects={client_files.monthlyTitles}
                  //   update_input_objects={(key_to_be_updated, file) => {
                  //     handle_client_file_update("monthlyTitles", key_to_be_updated, file);
                  //   }}
                  // />
                }
              </Box>
            ) : activeStep === 1 ? (
              <Box>
                {
                  <InputTable
                    stage_name={steps[activeStep]}
                    input_objects={client_files.masterTitles}
                    update_input_objects={(key_to_be_updated, file) => {
                      handle_client_file_update("masterTitles", key_to_be_updated, file);
                    }}
                  />
                }
              </Box>
            ) : (
              <ParametersForm handle_submit={handle_submit} parameters={parameters} handle_slider_change={handle_slider_change} handle_input_change={handle_input_change} activeStep={activeStep} ready_to_generate_schedule={ready_to_generate_schedule} />
            )}
          </Box>

          <Box className="flex justify-end gap-3 my-2">
            <Button variant="outlined" sx={{}} disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button variant="outlined" disabled={activeStep === 2} sx={{}} onClick={handleNext}>
              Next
            </Button>
            {/* {
              activeStep < 2 ?
                <Button variant="outlined" disabled={activeStep === 2} sx={{}} onClick={handleNext}>
                  Next
                </Button>
                : <ContainedButton onClick={handle_submit} type="submit" disabled={activeStep < 2 || !ready_to_generate_schedule
                } className={`disabled:bg-slate-200`} sx={{
                  color: "white"
                }} title={ready_to_generate_schedule ? "Click to generate schedule" : 'Please Upload All Files to generate schedule'}>
                  {ready_to_generate_schedule ? 'Generate Schedule' : 'Please upload all files to generate schedule'}
                </ContainedButton>
            } */}
            {/* {activeStep < 2 && (
            )} */}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default InputFormContainer;